import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./AuthContext";
import { ThemeProvider } from "./ThemeContext";
import InstallPrompt from "./components/InstallPrompt";
import ScrollToTop from "./components/ScrollToTop";
import { useGA } from "./lib/analytics";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Community from "./pages/Community";
import Contact from "./pages/Contact";
import Compare from "./pages/Compare";
import Pricing from "./pages/Pricing";
import ExampleWebsite from "./pages/ExampleWebsite";
import Services from "./pages/Services";
import BoardTrainForm from "./pages/BoardTrainForm";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import StatesIndex from "./pages/StatesIndex";
import StateLanding from "./pages/StateLanding";
import RegionLanding from "./pages/RegionLanding";
import { CANADA, WORLDWIDE, UNITED_KINGDOM } from "./data/regions";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";

import { ClientLayout, AdminLayout } from "./components/PortalLayout";
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientDogs from "./pages/client/ClientDogs";
import BookingCalendar from "./pages/client/BookingCalendar";
import ClientBookings from "./pages/client/ClientBookings";
import Membership from "./pages/client/Membership";

import AdminDashboard from "./pages/admin/AdminDashboard";
import TrainerDashboard from "./pages/admin/TrainerDashboard";
import AdminRunGrid from "./pages/admin/AdminRunGrid";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminClients from "./pages/admin/AdminClients";
import POS from "./pages/admin/POS";
import AdminReports from "./pages/admin/AdminReports";
import TrainerGrid from "./pages/admin/TrainerGrid";
import TrainingBookings from "./pages/admin/TrainingBookings";
import ClientDetail from "./pages/admin/ClientDetail";
import TeamChat from "./pages/admin/TeamChat";
import Messages from "./pages/Messages";
import AdminGallery from "./pages/admin/AdminGallery";
import AdminMarketing from "./pages/admin/AdminMarketing";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminSocial from "./pages/admin/AdminSocial";
import AdminCourses from "./pages/admin/AdminCourses";
import AdminVideoCalls from "./pages/admin/AdminVideoCalls";
import AdminLiveEvents from "./pages/admin/AdminLiveEvents";
import CallPage from "./pages/CallPage";
import PublicEvent from "./pages/PublicEvent";
import Contracts from "./pages/client/Contracts";
import ClientGallery from "./pages/client/ClientGallery";
import ClientProfile from "./pages/client/ClientProfile";
import ClientCourses from "./pages/client/ClientCourses";
import ClientCourseDetail from "./pages/client/ClientCourseDetail";

const BookRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/client/book" }} replace />;
  if (user.role === "CLIENT") return <Navigate to="/client/book" replace />;
  return <Navigate to="/admin/run-grid" replace />;
};

const RoleHomeAdmin = () => {
  const { user } = useAuth();
  if (user?.role === "TRAINER") return <TrainerDashboard />;
  return <AdminDashboard />;
};

const MessagesRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "CLIENT" ? "/client/messages" : "/admin/messages"} replace />;
};

/**
 * GAListener — invisible component that fires a Google Analytics 4
 * page_view every time the SPA route changes. Must be rendered INSIDE
 * <BrowserRouter> so useLocation() works. Renders no DOM.
 */
const GAListener = () => {
  useGA();
  return null;
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <Toaster richColors position="top-right" />
      <BrowserRouter>
        <ScrollToTop />
        <GAListener />
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/call/:id" element={<CallPage />} />
            <Route path="/event/:slug" element={<PublicEvent />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/community" element={<Community />} />
            <Route path="/contact" element={<Contact />} />
            {/* Comparison / competitor SEO landing page */}
            <Route path="/compare" element={<Compare />} />
            <Route path="/dog-training-software-comparison" element={<Compare />} />
            {/* Pricing */}
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/services" element={<Services />}/>
            {/* Example Website — consolidated demo (Board & Train + Memberships + Financing).
                Also aliased under /website-example for legacy links. */}
            <Route path="/example-website" element={<ExampleWebsite />} />
            <Route path="/website-example" element={<ExampleWebsite />} />
            <Route path="/board-train-form" element={<BoardTrainForm />} />
            <Route path="/book" element={<BookRedirect />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancel" element={<PaymentCancel />} />
            <Route path="/messages" element={<MessagesRedirect />} />

            {/* Local SEO — dog training software by state */}
            <Route path="/dog-training-software" element={<StatesIndex />} />
            {/* Regional pages — literal routes MUST come before the dynamic
                :state route so React Router matches them first. */}
            <Route path="/dog-training-software/canada" element={<RegionLanding region={CANADA} />} />
            <Route path="/dog-training-software/uk" element={<RegionLanding region={UNITED_KINGDOM} />} />
            <Route path="/dog-training-software/worldwide" element={<RegionLanding region={WORLDWIDE} />} />
            <Route path="/dog-training-software/:state" element={<StateLanding />} />

            {/* Blog */}
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            <Route path="/client" element={<ClientLayout />}>
              <Route index element={<ClientDashboard />} />
              <Route path="dogs" element={<ClientDogs />} />
              <Route path="book" element={<BookingCalendar />} />
              <Route path="bookings" element={<ClientBookings />} />
              <Route path="messages" element={<Messages />} />
              <Route path="profile" element={<ClientProfile />} />
              <Route path="contracts" element={<Contracts />} />
              <Route path="gallery" element={<ClientGallery />} />
              <Route path="membership" element={<Membership />} />
              <Route path="courses" element={<ClientCourses />} />
              <Route path="courses/:id" element={<ClientCourseDetail />} />
            </Route>

            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<RoleHomeAdmin />} />
              <Route path="dashboard" element={<RoleHomeAdmin />} />
              <Route path="trainer-dashboard" element={<TrainerDashboard />} />
              <Route path="run-grid" element={<AdminRunGrid />} />
              <Route path="trainer-grid" element={<TrainerGrid />} />
              <Route path="training" element={<TrainingBookings />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="clients/:id" element={<ClientDetail />} />
              <Route path="pos" element={<POS />} />
              <Route path="messages" element={<Messages />} />
              <Route path="team-chat" element={<TeamChat />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="marketing" element={<AdminMarketing />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="social" element={<AdminSocial />} />
              <Route path="courses" element={<AdminCourses />} />
              <Route path="video-calls" element={<AdminVideoCalls />} />
              <Route path="live-events" element={<AdminLiveEvents />} />
              <Route path="my-dogs" element={<TrainerDashboard />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <InstallPrompt />
        </AuthProvider>
      </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
